/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { AiResponse, ChatBotWrite, GenerativeWrite } from '@squiz/dxp-ai-client';
import { NotEmptyValidator, TextLengthValidator } from '@squiz/dxp-console-support';
import {
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_DEFAULT,
  BUTTON_THEME_SUCCESS,
  Button,
  FORM_THEME_LITE,
  Form,
  TextField,
  TextFieldProps,
} from '@squiz/sds';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { from } from 'rxjs/internal/observable/from';
import { AI_STRINGS } from '../../../Constants/Strings.constants';
import { getAiService } from '../../../Services/ai';
import { isGenerative } from '../../../Utilities/formPredicates';
import { navigateToAiListItem } from '../../AiDataList/utils/navigateToAiListItem';
import { CREATE_TOOL_DATA_IDS, CREATE_TOOL_FORM_IDS } from '../CreateToolDialog.constants';
import { CreateToolDialogFormProps } from './CreateToolDialogForm.props';

export const nameLengthConstraints = {
  max: 50,
  min: 1,
};

// TODO: rename this component to be something like CreateAiDialogForm
/**
 * The button for editing a content tool.
 * @param {CreateToolDialogFormProps} props Props passed to `<CreateToolDialogForm />`.
 * @returns {JSX.Element} The `<CreateToolDialogForm />` component.
 */
export const CreateToolDialogForm = (props: CreateToolDialogFormProps): JSX.Element => {
  const { t } = useTranslation();
  const { setButtonKeys, setButtons, close } = props;
  const navigate = useNavigate();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  const aiService = getAiService();
  const contentToolNameRef = useRef<HTMLInputElement>(null);

  const labels = useMemo(() => {
    return {
      apiError: t(AI_STRINGS.apiError),
      cancel: t(AI_STRINGS.cancel),
      create: t(AI_STRINGS.create),
      createTool: t(AI_STRINGS.createTool),
      name: t(AI_STRINGS.name),
      nameFieldDescription: isGenerative(props.type)
        ? t(AI_STRINGS.nameFieldDescriptionGenerative)
        : t(AI_STRINGS.nameFieldDescriptionChatbot),
      nameLengthMessage: t(AI_STRINGS.nameFieldLength),
      required: t(AI_STRINGS.requiredField),
    };
  }, [props.type, t]);

  const notEmptyValidator = useMemo(() => {
    return new NotEmptyValidator<boolean>((): boolean => false);
  }, []);

  const textLengthValidator = useMemo(() => {
    return new TextLengthValidator<boolean>(
      () => false,
      () => true,
      nameLengthConstraints,
    );
  }, []);

  const [makingRequest, setMakingRequest] = useState<boolean>(false);
  const [formError, setFormError] = useState<string>(``);

  const validateFields = useCallback(
    (inputs: Array<HTMLInputElement>): void => {
      const nextErrors = { ...fieldErrors };

      inputs.forEach((input: HTMLInputElement): void => {
        const inputValue = input.value;
        const inputFieldId = input.getAttribute(`id`) as string;

        if (!notEmptyValidator.validate(String(inputValue))) {
          nextErrors[inputFieldId] = labels.required;
        } else if (!textLengthValidator.validate(String(inputValue))) {
          nextErrors[inputFieldId] = labels.nameLengthMessage;
        } else {
          delete nextErrors[inputFieldId];
        }
      });

      setFieldErrors(nextErrors);
    },
    [fieldErrors, labels, notEmptyValidator, setFieldErrors, textLengthValidator],
  );

  const errorChecker = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      validateFields([event.target]);
    },
    [validateFields],
  );

  const submitHandler = useCallback(() => {
    setMakingRequest(true);
    const inputs = {
      contentToolName: contentToolNameRef.current as HTMLInputElement,
    };
    if (!notEmptyValidator.validate(String(inputs.contentToolName.value))) {
      const inputsToCheck = [inputs.contentToolName];
      validateFields(inputsToCheck);
      setMakingRequest(false);
      return;
    }

    if (!textLengthValidator.validate(String(inputs.contentToolName.value))) {
      const inputsToCheck = [inputs.contentToolName];
      validateFields(inputsToCheck);
      setMakingRequest(false);
      return;
    }

    setFieldErrors({});

    if (isGenerative(props.type)) {
      const newGenerativeAi: GenerativeWrite = {
        authType: 'dxp',
        enabled: false,
        instruction: '',
        name: inputs.contentToolName.value,
        responseTemplate: '',
        type: 'generative',
      };

      lastValueFrom(from(aiService.apiClient.generative.create(newGenerativeAi)))
        .then((response: AiResponse) => {
          setMakingRequest(false);
          close();
          navigateToAiListItem(response, navigate);
          return true;
        })
        .catch((_) => {
          setFormError(labels.apiError);
          setMakingRequest(false);
        });
    } else {
      const newChatBotAi: ChatBotWrite = {
        authType: 'none',
        enabled: false,
        instruction: '',
        name: inputs.contentToolName.value,
        responseTemplate: '',
        type: 'chatbot',
      };

      lastValueFrom(from(aiService.apiClient.chatbot.create(newChatBotAi)))
        .then((response: AiResponse) => {
          setMakingRequest(false);
          close();
          navigateToAiListItem(response, navigate);
          return true;
        })
        .catch((_) => {
          setFormError(labels.apiError);
          setMakingRequest(false);
        });
    }
  }, [
    aiService.apiClient.chatbot,
    aiService.apiClient.generative,
    close,
    labels.apiError,
    navigate,
    notEmptyValidator,
    props.type,
    textLengthValidator,
    validateFields,
  ]);

  const inputValues = {
    contentToolName: '',
  };

  const contentToolNameFieldProps: TextFieldProps = {
    errorMessage: fieldErrors[CREATE_TOOL_FORM_IDS.contentToolName] || null,
    fieldLabel: labels.name,
    id: CREATE_TOOL_FORM_IDS.contentToolName,
    name: CREATE_TOOL_FORM_IDS.contentToolName,
    onChange: (event: ChangeEvent<HTMLInputElement>): void => {
      errorChecker(event);
    },
    required: true,
    value: inputValues.contentToolName,
  };

  useEffect(() => {
    const cancelButton = (
      <Button
        classes="dialog-cancel-btn"
        dataId={CREATE_TOOL_DATA_IDS.cancelButton}
        format={BUTTON_FORMAT_SOFT}
        key={'cancel-btn'}
        onClick={(): void => {
          close();
        }}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />
    );

    const createButton = (
      <Button
        classes="dialog-create-btn"
        dataId={CREATE_TOOL_DATA_IDS.createButton}
        format={BUTTON_FORMAT_STRONG}
        isLoading={makingRequest}
        key={'create-btn'}
        onClick={(): void => submitHandler()}
        text={labels.create}
        theme={BUTTON_THEME_SUCCESS}
      />
    );

    if (makingRequest) {
      setButtonKeys([`create`]);
      setButtons([createButton]);
    } else {
      setButtonKeys([`cancel`, `create`]);
      setButtons([cancelButton, createButton]);
    }
  }, [close, labels.cancel, labels.create, makingRequest, setButtonKeys, setButtons, submitHandler]);

  return (
    <Form errorMessage={formError} legend={''} theme={FORM_THEME_LITE}>
      <TextField
        {...contentToolNameFieldProps}
        data-id={CREATE_TOOL_DATA_IDS.contentToolName}
        note={labels.nameFieldDescription}
        ref={contentToolNameRef}
      />
    </Form>
  );
};
